import SoundPillIcon from '../../../assets/logo/SoundPillIcon.svg';
import ResetAtSixCountdown from '../../../components/landing/ResetAtSixCountdown';
import { useNavigate } from 'react-router-dom';
import { onClickPageButtonTracking, usePageTracking, useScrollTracking } from '../../../hooks/pageTracking';
import { LandingButtonClickActionType, UserActionEnum } from '../../../types/customLogger';
import { userAnalytics } from '../../../utils/analytics/userAnalytics';
import { setPostAuthLandingRedirect } from '../../../utils/postAuthLandingRedirect';
import { useRef } from 'react';
import { SLEEP_EXPERT_CARDS as EXPERT_CARDS } from './constants';

const PAGE_NAME = 'lp_sleep_subscription';
const START_BUTTON_LABEL = '1개월 무료체험 하러가기';

export default function LpSubscription() {
    const scrollRef = useRef<HTMLDivElement>(null);
  
    usePageTracking(PAGE_NAME);
    useScrollTracking(scrollRef, PAGE_NAME);
    
  const navigate = useNavigate();

  const onClickStartFreeTrial = () => {
    onClickPageButtonTracking(PAGE_NAME, START_BUTTON_LABEL);
    const action = `${UserActionEnum.LANDING_BUTTON_CLICK}_${START_BUTTON_LABEL}` as LandingButtonClickActionType;
    userAnalytics(action, PAGE_NAME);
    setPostAuthLandingRedirect('/landing/sleep/subscription-benefit');
    navigate('/auth');
  };

  return (
    <div ref={scrollRef} className="h-[100dvh] overflow-y-auto bg-[#101010]">
      <div className="mx-auto w-full max-w-[1200px]">
        <section className="bg-[#22ABFF] px-4 pb-4 pt-10 text-center md:px-8 md:pt-14">
          <p className="text-[clamp(20px,3.2vw,40px)] font-bold leading-[1.25] text-[#FCFF68]">
            여기까지 찾아온 당신,
            <br />
            보통 행운아가 아니네요.
          </p>
          <img
            src="/landing/sleep/lp-subscription-mobile.png"
            alt="1개월 무료 쿠폰 랜딩 상단 이미지"
            className="mx-auto mt-4 block w-full h-auto max-w-[787px] pt-10"
          />
        </section>

        <section className="bg-[#22ABFF] px-4 pb-8 pt-3 text-center md:px-8 md:pb-12 pt-14">
          <div className="mx-auto w-full max-w-[1040px]">
            <div className="mx-auto inline-block bg-[#FCFF68] px-[clamp(8px,1.3vw,14px)] py-[clamp(4px,0.8vw,8px)]">
              <p className="text-[clamp(24px,5vw,58px)] font-bold leading-[1.1] text-[#0086FF]">
                이 창을 나가면
                <span className="block sm:inline"> 혜택이 사라져요</span>
              </p>
            </div>

            <p className="mt-[clamp(18px,2.6vw,32px)] text-[clamp(24px,4.6vw,54px)] font-semibold leading-[1.3] text-white">
              지금 링크에서 사운드필을 시작해야
              <br />
              비밀 혜택이 적용됩니다.
            </p>

            <ResetAtSixCountdown className="mt-[clamp(20px,3.2vw,34px)] max-w-[980px]" />
          </div>
        </section>

        <section className="relative overflow-hidden bg-gradient-to-b from-[#22ABFF] via-[#123655] to-[#101010] px-4 pb-20 pt-10 text-center md:px-8 md:pt-14">
          <p className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 whitespace-nowrap text-[clamp(48px,12vw,180px)] font-bold text-[#7EDCFF]/20">
            Sound Pill
          </p>

          <div className="relative mx-auto mt-16 flex h-[clamp(112px,22vw,190px)] w-[clamp(112px,22vw,190px)] items-center justify-center rounded-[20px] bg-white p-2 shadow-[0_18px_40px_rgba(0,0,0,0.35)]">
            <img src={SoundPillIcon} alt="SoundPill 아이콘" className="w-full" />
          </div>

          <div className="mx-auto mt-5 inline-flex h-[clamp(60px,13vw,130px)] w-[clamp(140px,27.7vw,277px)] items-center justify-center rounded-[10px] bg-[#101010] px-[clamp(18px,4vw,30px)]">
            <span className="whitespace-nowrap font-['Pretendard'] text-[clamp(32px,6.4vw,64px)] font-semibold leading-[1.295] tracking-[-0.02em] text-[#FFFFFF]">
              사운드필
            </span>
          </div>

          <p className="mt-16 text-[clamp(40px,8.2vw,78px)] font-bold leading-none text-[#FFFFFF]">제작기간 3년</p>
          <p className="mt-10 text-[clamp(24px,4.4vw,52px)] leading-[1.3] text-[#FFFFFF]">
            임상시험 기반
            <br />
            근거 있는 브레인 사운드는
          </p>
          <p className="mt-16 text-[clamp(42px,8.8vw,108px)] font-bold leading-[1.2] tracking-[-0.03em] text-[#FFFFFF]">
            1개월만 들어도
            <br />
            <span className="bg-[#2C3779] px-2 text-[#FFFFFF]">수면 뇌로</span> 바뀌어요.
          </p>
        </section>

        <section className="bg-[#101010] px-4 pb-24 pt-8 text-center md:px-8">
          <div className="mx-auto w-full max-w-[1040px]">
            <img
              src="/landing/sleep/lp-5078-brain-graphic.png"
              alt="뇌과학 수면 사운드 그래픽"
              className="mx-auto block w-full h-auto max-w-[733px]"
            />

            <p className="mt-8 text-[clamp(22px,4.2vw,50px)] leading-[1.4] tracking-[-0.025em] text-white">
              뇌에 영향을 주는 특정 주파수와
              <br />
              사운드 요소를 결합한
              <br />
              <strong className="font-bold bg-[#2C3779]">‘뇌과학 수면 사운드’</strong>
            </p>
            <p className="mt-10 text-[clamp(22px,4.2vw,50px)] leading-[1.4] tracking-[-0.025em] text-white">
              스트레스 해소, 수면을 위한 안정에
              <br />
              대한 효과가 입증되었어요.
            </p>

            <p className="mt-8 leading-[1.4] tracking-[-0.02em] text-[#6B7684] text-m">
              의료 전문 자문기업 ‘메디허브’의 의료진과 함께한 SR&META 임상시험 진행 결과
            </p>

            <img
              src="/landing/sleep/lp-5078-graphs.png"
              alt="임상시험 전후 비교 그래프"
              className="mx-auto mt-16 block w-full h-auto max-w-[442px]"
            />

            <div className="mx-auto mt-12 flex h-[clamp(56px,8vw,139px)] w-full max-w-[1040px] items-center justify-center rounded-[13px] bg-[#4F67F8]">
              <p className="text-[clamp(22px,4.8vw,48px)] font-semibold leading-[1.295] tracking-[-0.02em] text-white">
                왜 사운드필이어야 할까요?
              </p>
            </div>

            <img
              src="/landing/sleep/lp-5078-reason-1.png"
              alt="첫 번째 이유: 의학적 근거기반 뇌과학 사운드"
              className="mx-auto mt-10 block w-full h-auto max-w-[902px]"
            />

            <img
              src="/landing/sleep/lp-5078-reason-4.png"
              alt="두 번째 이유: 부작용 없는 과학적 해결"
              className="mx-auto mt-10 block w-full h-auto max-w-[902px]"
            />

            <div className="relative mx-auto mt-10 w-full  overflow-hidden rounded-[8px]   px-4 pb-8 pt-10 md:px-10 md:pt-12">
              <img
                src="/landing/sleep/lp-5078-point1-bg.png"
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-[rgba(3,8,28,0.35)]" />

              <div className="relative">
                <div className="mx-auto mt-32 inline-flex h-[clamp(34px,5vw,62px)] items-center justify-center rounded-full bg-[#4F67F8] px-[clamp(20px,4vw,36px)]">
                  <span className="whitespace-nowrap text-[clamp(22px,3.2vw,42px)] font-semibold leading-none tracking-[-0.02em] text-[#F3F6FF]">
                    POINT 01
                  </span>
                </div>

                <p className="mt-8 text-[clamp(34px,7.8vw,74px)] font-bold leading-[1.15] tracking-[-0.03em] text-white">
                  의료전문가들이
                </p>
                <p className="text-[clamp(34px,7.8vw,74px)] font-bold leading-[1.15] tracking-[-0.03em] text-[#8798FF]">
                  직접 개발에 참여했습니다
                </p>

                <p className="mt-8 text-[clamp(20px,3.8vw,42px)] font-medium leading-[1.35] tracking-[-0.02em] text-[#CFD5E3]">
                  국내 의료 전문가들이
                  <br />
                  사운드필에 직접 참여했어요
                </p>

                <img
                  src="/landing/sleep/lp-5078-point1-laurels.png"
                  alt="보건복지부 승인 5개의 효과, 3건의 논문 출판"
                  className="mx-auto mt-8 block w-full h-auto max-w-[756px]"
                />
              </div>
            </div>

            <div className="mx-auto mt-16 w-full max-w-[879px] space-y-[clamp(18px,3.4vw,32px)]">
              {EXPERT_CARDS.map((expert) => (
                <article
                  key={expert.key}
                  className="w-full rounded-[clamp(14px,2.4vw,30px)] bg-[#F6F6F9] px-[clamp(12px,2.6vw,24px)] py-[clamp(12px,2.6vw,26px)] text-left text-[#303030] shadow-[0_4px_13px_rgba(0,0,0,0.18)]"
                >
                  <div className="flex items-start gap-[clamp(10px,2vw,24px)]">
                    <img
                      src={expert.imageSrc}
                      alt={`${expert.name} 프로필`}
                      className="h-[clamp(74px,19vw,250px)] w-[clamp(74px,19vw,250px)] shrink-0 rounded-full object-cover"
                    />

                    <div className="min-w-0">
                      <p className="text-[clamp(20px,4.4vw,35px)] font-semibold leading-[1.295]">{expert.name}</p>
                      <p className="mt-1 text-[clamp(13px,3.2vw,27px)] font-semibold leading-[1.295] text-[#686868]">
                        {expert.title}
                      </p>

                      <div className="mt-[clamp(8px,1.8vw,14px)] inline-flex rounded-full bg-[#E4EAFF] px-[clamp(10px,2.2vw,23px)] py-[clamp(3px,0.8vw,6px)]">
                        <span className="text-[clamp(12px,2.7vw,25px)] font-semibold leading-[1.295] text-[#3B3B3B]">
                          {expert.badge}
                        </span>
                      </div>

                      <p className="mt-[clamp(10px,2vw,18px)] text-[clamp(13px,2.7vw,30px)] font-medium leading-[1.35] text-[#303030]">
                        {expert.prefix}
                        <span className="font-bold text-[#000AFF]">{expert.highlight}</span>
                        {expert.suffix}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

                  <div className="mx-auto mt-8 w-full max-w-[1024px]  py-[clamp(52px,12vw,84px)]">
              <div className="mx-auto inline-flex h-[clamp(42px,7.2vw,68px)] min-w-[clamp(170px,44vw,262px)] items-center justify-center rounded-full bg-[#4F67F8] px-[clamp(24px,6vw,44px)]">
                <span className="whitespace-nowrap text-[clamp(20px,5.4vw,49px)] font-semibold leading-none tracking-[-0.02em] text-[#F3F6FF]">
                  POINT 02
                </span>
              </div>
            </div>

            <div className="mt-14 text-center">
              <p className="text-[clamp(34px,7vw,72px)] font-bold leading-[1.2] tracking-[-0.02em] text-[#F1F1F1]">
                이미 테스트로 검증된
              </p>
              <p className="mt-2 text-[clamp(34px,7vw,72px)] font-bold leading-[1.2] tracking-[-0.02em] text-[#8798FF]">
                사운드필의 효과
              </p>
              <p className="mt-6 text-[clamp(20px,3.8vw,40px)] font-medium leading-[1.35] tracking-[-0.02em] text-[#939393]">
                사운드필을 사용한 83% 이상의
                <br />
                사람들이 수면에 도움이 됐습니다!
              </p>
            </div>

            <img
              src="/landing/sleep/lp-5078-validated-people.png"
              alt="사운드필 사용자의 수면 개선 검증 데이터"
              className="mx-auto mt-32 block w-full h-auto max-w-[873px]"
            />

            <div className="mt-12 text-center">
              <p className="text-[clamp(34px,8vw,100px)] font-bold leading-[1.35] text-[#8798FF]">제작기간 3년</p>
              <p className="text-[clamp(34px,8vw,100px)] font-bold leading-[1.35] text-[#28C8A8]">사운드에 진심인</p>
              <p className="mt-6 text-[clamp(24px,5.2vw,64px)] font-medium leading-[1.4] text-white">전문가들이 모여 만든</p>
              <p className="mt-4 text-[clamp(34px,8vw,100px)] font-bold leading-[1.3] text-white">
                <span className="inline-block border-b-[clamp(12px,2.4vw,30px)] border-[#4F68FC]">과학적 해결법</span>
              </p>
            </div>

            <div className="mx-auto mt-24 inline-block bg-[#FCFF68] px-[clamp(10px,1.5vw,18px)] py-[clamp(4px,0.8vw,8px)]">
              <p className="text-[clamp(18px,4.2vw,50px)] font-semibold leading-[1.295] tracking-[-0.02em] text-[#0086FF]">
                이 링크에서만 1개월 무료!
              </p>
            </div>
              <p className="mt-6 text-[clamp(24px,5.2vw,64px)] font-medium leading-[1.4] text-white">지금 체험해 보세요</p>

            <ResetAtSixCountdown className="mt-12 max-w-[980px]" />

            <button
              type="button"
              onClick={onClickStartFreeTrial}
              className="mt-24 mb-6 w-full rounded-full bg-[#4F68FC] px-6 py-[clamp(18px,3.8vw,42px)] text-[clamp(20px,4.8vw,48px)] font-semibold leading-[1.2] tracking-[-0.02em] text-white"
            >
              1개월 무료체험 하러가기
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
